import * as React from 'react';

import Alert from 'antd/es/alert';
import { NavLink, useLocation } from 'react-router-dom';

import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import ExternalHtml from '@common/react/components/UI/ExternalHtml/ExternalHtml';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { useMobileView } from '@common/react/hooks/useMobileView';
import { ItemsProvider, SortingDirection } from '@common/react/components/Core/ItemsProvider/ItemsProvider';

import { Region } from '@app/objects/Region';
import InquiryForm from '@app/components/Forms/InquiryForm';
import { User } from '@app/objects/User';
import LoginForm from '@app/components/UI/ScheduleAppointmentModal/LoginForm';
import DoctorCard from '@app/components/UI/DoctorCard/DoctorCardHome';
import { Special } from '@app/objects/Special';
import SpecialsCarousel from '@app/components/UI/SpecialsCarousel/SpecialsCarousel';
import RegionTag from '@app/components/Pages/Region/RegionTag';
import ClinicCard from '@app/components/Pages/Clinics/ClinicCard';

import '@app/scss/pages/home.scss';
import '@app/scss/pages/doctor.scss';
import '@app/scss/pages/clinicsPage.scss';
import '@app/scss/pages/region.scss';

const navMenu = [
	{
		title: 'About',
		check: (item) => !!item?.description,
		id: 'about',
	},
	{
		title: 'Book Online',
		check: (item, isMobile) => isMobile,
		id: 'bookOnline',
		className: 'show-mobile show-mobile__inline-block',
	},
	{
		title: 'Clinics',
		check: (item) => item?.locations?.length > 0,
		id: 'locations',
	},
	{
		title: 'Providers',
		check: (item) => item?.doctors?.length > 0,
		id: 'doctors',
	},
	{
		title: 'Specials',
		check: (item) => item?.specials?.length > 0,
		id: 'specials',
		className: '',
	},
];

const RegionInner: React.FC<{region: Region}> = ({ region }) => {
	const { getUser } = useApplicationContext();
	const user = getUser<User>();
	const location = useLocation();

	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [loginVisible, setLoginVisible] = React.useState(false);
	const toggle = () => setIsOpen(!isOpen);
	const locationsForInquiry = region.locations as any || [];
	const isMobile = useMobileView(768);
	const specials = region.specials?.map((item) => ({
		...item,
		location: item.location || region.locations.find((location) => location.id === item.locationId),
	}));

	const inquiry = (<div className="inquiry-wrapper">
		<div className="contact-text">
			<Alert
				title={<>
					Leave inquiry and clinic front desk will contact you soon for help.
					{!user ? <>
						<br />
						Already have account? -
						{' '}
						<a onClick={() => setLoginVisible(true)}>Login</a>
						{' '}
						for fast fill form.
					</> : null}
				</>
				}
				type="info"
			/>
		</div>
		<InquiryForm
			locations={locationsForInquiry}
			doctors={region?.doctors ?? []}
			doctorId={region?.doctors ? region?.doctors?.[0]?.id : undefined}
		/>
	</div>);

	return <>
		<LoginForm visible={loginVisible} handleClose={() => setLoginVisible(false)} afterLogin={() => setLoginVisible(false)} />
		<div className="site-headline doctor-portal region-page">
			<div className="doctor-portal-container">
				<div className="px-50 mb20 doctor-portal-content">
					<div className="max-content main-content">
						<BreadCrumbs
							links={[
								{
									title: 'Locations',
									path: '/locations',
								},
								...region?.parents.map((region) => ({
									title: region.name,
									path: `/locations/${region.path}`,
								})),
							]}
							title={region?.name}
						/>
						<div className="mb20 px15 main-content-block" style={{ marginBottom: 20 }}>
							<div
								className="doctor-menu-content"
								style={{
									flexDirection: 'row', flexWrap: 'wrap', gap: 15, alignItems: 'center',
								}}
							>
								<h1 style={{ margin: 0 }}>{region?.name}</h1>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										flexWrap: 'wrap',
										gap: 15,
										alignItems: 'center',
										marginLeft: 'auto',
										width: isMobile ? '100%' : undefined,
									}}
								>
									{region.children.map((region) => <RegionTag
										tagClassName="title__second"
										style={{ margin: 0 }}
										key={region.id}
										location={region}
									/>)}
								</div>
								<nav className="doctor-portal__nav pull-right" style={{ margin: 0 }}>
									<ul>
										{navMenu.filter((value) => value.check(region, isMobile)).map((item) =>
											<li key={item.id} className={item.className}>
												<a href={`${location?.pathname}#${item.id}`}>{item.title}</a>
											</li>)
										}
									</ul>
								</nav>
							</div>
						</div>
						<div className="doctor-other-content">
							{region?.description && <div className="doctor-portal__about scroll-top" id="about">
								<h3>
									About
									{' '}
									{region?.name}
								</h3>

								{region?.description
									? <div className="inner-page__headline column-md-4 mb10">
										<div className="column-md-4">
											<ExternalHtml html={region?.description} />
										</div>
									</div> : <div className="mb10">-</div>
								}
							</div>}
							<div className="show-touch show-touch__block">
								<h3 id="bookOnline" className="scroll-top">Book Online</h3>
								<div className="schedule-appointment__form">
									{inquiry}
								</div>
							</div>
							{region.locations?.length > 0 ? <h3 id="locations" className="scroll-top">Clinics</h3> : null}
							{locationsForInquiry?.length ? <div className="clinics-page">
								{locationsForInquiry.map((item) => <ClinicCard key={item.id} location={item} />)}
							</div> : null}
							{region.doctors && region.doctors.length > 0 && <div>
								<br />
								<h2 className="scroll-top" id="doctors">Providers</h2>
								<div className="home__container doctor-card-list" style={{ paddingLeft: 0, paddingRight: 0 }}>
									<div className="mb10 doctors-list">
										{region.doctors.map((doctor) => <DoctorCard
											doctor={doctor as any}
											key={doctor.id}
										/>)}
									</div>
								</div>
							</div>}
							<ItemsProvider<Special>
								loadRequest="specialsList"
								items={specials}
								filters={{
									regionId: region.id,
									column: [
										{ caption: 'name', direction: SortingDirection.Ascending },
										{ caption: 'price', direction: SortingDirection.Default }],
								}}
							>
								{({ state: { items } }) => <>
									{items.length > 0 ? <div className="is-relative">
										<div className="scroll-top" id="specials" />
										<NavLink
											className="btn btn-primary btn-more pull-right"
											to={{ pathname: '/specials', search: `regionIds=[${region.id}]` }}
										>
											View All
										</NavLink>
										<SpecialsCarousel specials={items} count={3} titleAsH2 />
									</div> : null}
								</>}
							</ItemsProvider>
						</div>
					</div>
					<div className="schedule-appointment__wrapper mobile-hide">
						<div className={`schedule-appointment__form ${
							isOpen ? 'schedule-appointment__open' : 'schedule-appointment__close'} schedule-appointment__inquiry-tab`}
						>
							<button
								type="button"
								className="btn btn-sm btn-default schedule-appointment__button pull-right"
								onClick={toggle}
							>
								<span />
							</button>
							{inquiry}
						</div>
					</div>
				</div>
			</div>
		</div>
	</>;
};

const RegionPage: React.FC = () => {
	return (
		<ServerPageProvider
			path="locations"
			reloadByPathChange
			defaultTitle="Location"
			inner={(page) => (
				<RegionInner
					region={page?.item}
				/>
			)}
		/>
	);
};

export default RegionPage;
